import { User, Package, SquaresFour } from '@phosphor-icons/react'
import { AccountActivePage, AccountPageExpandedSection, AccountPageUIProps } from '../types'
import { useAccountPageUIContext } from '../AccountPageProvider'
import { useAccountCustomPages } from '../hooks/useAccountCustomPages'
import ProfileLogo from '../utility-components/ProfileLogo'
import { hexToRgba } from '../../utils/colorUtils'

const getInitials = (name: string) =>
  name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()

const getFirstName = (name: string) => name.split(' ')[0]

const TABS = [
  { page: AccountActivePage.PROFILE, section: AccountPageExpandedSection.PROFILE, label: 'Profile', Icon: User },
  { page: AccountActivePage.ORDERS, section: AccountPageExpandedSection.ORDERS, label: 'Orders', Icon: Package },
]

type NavTab = {
  page: string
  label: string
  Icon: typeof User
  onSelect: (actions: AccountPageUIProps['actions']) => void
}

// Known pages first, then one tab per custom page in config order.
const useNavTabs = (): NavTab[] => {
  const customPages = useAccountCustomPages()
  return [
    ...TABS.map(({ page, section, label, Icon }) => ({
      page,
      label,
      Icon,
      onSelect: (actions: AccountPageUIProps['actions']) => {
        actions.onTabChange(section)
        actions.onSectionClick?.(AccountPageExpandedSection.NAVIGATION)
      },
    })),
    ...customPages.map(({ type, label }) => ({
      page: type,
      label,
      Icon: SquaresFour,
      onSelect: (actions: AccountPageUIProps['actions']) => actions.onPageChange?.(type),
    })),
  ]
}

export const DesktopSidebar = () => {
  const { uiConfig: config, activePage, data, actions } = useAccountPageUIContext()
  const { navigation } = config
  const user = data?.profilePage?.user
  const initials = user?.name ? getInitials(user.name) : ''
  const firstName = user?.name ? getFirstName(user.name) : 'there!'
  const tabs = useNavTabs()

  return (
    <div
      className='ui-flex ui-flex-col ui-w-[312px] ui-shrink-0 ui-overflow-clip ui-rounded-[10px] ui-h-[600px]'
      style={{ backgroundColor: navigation.background_color }}
    >
      {/* Profile header — bordered bottom */}
      <div
        className='ui-flex ui-flex-col ui-items-start ui-pt-[32px] ui-px-[24px] ui-border-b ui-border-solid ui-shrink-0 ui-w-full'
        style={{ borderColor: 'rgba(0,0,0,0.1)' }}
      >
        {/* Avatar area */}
        <div className='ui-h-[176px] ui-w-full ui-flex ui-items-center ui-justify-center'>
          <ProfileLogo size={82} initials={initials} />
        </div>

        {/* Greeting */}
        <div className='ui-w-full ui-flex ui-items-center ui-justify-center ui-pb-[24px] ui-pt-[12px]'>
          <div
            className='ui-flex ui-gap-[4px] ui-items-center ui-text-[24px] ui-leading-[32px] ui-tracking-[-0.48px]'
            style={{ color: navigation.text_color }}
          >
            <span className='ui-font-normal'>Hi</span>
            <span className='ui-font-semibold'>{firstName}</span>
          </div>
        </div>
      </div>

      {/* Nav items */}
      <div className='ui-flex ui-flex-col ui-px-[16px] ui-py-[20px]'>
        {tabs.map(({ page, label, Icon, onSelect }) => {
          const isActive = activePage === page
          return (
            <button
              key={page}
              className='ui-flex ui-items-center ui-gap-[8px] ui-p-[6px] ui-w-full ui-text-left ui-transition-all ui-duration-200'
              style={{
                backgroundColor: isActive ? hexToRgba(navigation.primary_color, 0.1) : 'transparent',
                borderRadius: navigation.tab_radius,
              }}
              onClick={() => onSelect(actions)}
            >
              <span
                className='ui-flex ui-items-center ui-p-[4px] ui-rounded-[8px]'
                style={{ opacity: isActive ? 1 : 0.4 }}
              >
                <Icon size={22} color={navigation.primary_color} weight='regular' />
              </span>
              <span
                className='ui-flex-1 ui-text-[16px] ui-leading-[24px] ui-tracking-[-0.16px]'
                style={{
                  color: navigation.text_color,
                  fontWeight: isActive ? 600 : 500,
                  opacity: isActive ? 1 : 0.6,
                }}
              >
                {label}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export const MobileBottomNav = () => {
  const { uiConfig: config, activePage, actions } = useAccountPageUIContext()
  const { navigation } = config
  const tabs = useNavTabs()

  return (
    <div
      className={`ui-flex ui-w-full ui-min-h-[52px] ui-items-center ${tabs.length > TABS.length ? 'ui-overflow-x-auto scrollbar-hide' : 'ui-overflow-clip'}`}
      style={{
        backgroundColor: navigation.background_color,
        // boxShadow: '0px 2px 10px -6px rgba(9,9,11,0.48)',
      }}
    >
      {tabs.map(({ page, label, Icon, onSelect }, i) => {
        const isActive = activePage === page
        return (
          <button
            key={page}
            onClick={() => onSelect(actions)}
            className='ui-flex ui-h-full ui-flex-1 ui-shrink-0 ui-whitespace-nowrap ui-items-center ui-justify-center ui-transition-all ui-duration-200'
            style={{ borderRight: i < tabs.length - 1 ? '1px solid rgba(9,9,11,0.06)' : 'none' }}
          >
            <span
              className='ui-flex ui-items-center ui-p-[4px] ui-rounded-[8px] ui-transition-all ui-duration-200'
              style={{
                backgroundColor: isActive ? hexToRgba(navigation.primary_color, 0.1) : 'transparent',
                opacity: isActive ? 1 : 0.4,
              }}
            >
              <Icon size={22} color={navigation.primary_color} weight='regular' />
            </span>
            <span
              className='ui-text-[14px] ui-font-semibold ui-leading-[22px] ui-tracking-[-0.14px] ui-pl-[4px] ui-pr-[12px] ui-transition-all ui-duration-200'
              style={{ color: navigation.text_color, opacity: isActive ? 1 : 0.6 }}
            >
              {label}
            </span>
          </button>
        )
      })}
    </div>
  )
}

const AccountPageSidebar = () => null

export default AccountPageSidebar
